/**
 * This week's classes across the seat's current courses (sprint decision Q1).
 *
 * The seat projection carries each course's next class and nothing more, so
 * the week is read the way All classes reads it: the course list, then each
 * course's sessions, under the SAME query keys, so Home and All classes share
 * one cache and returning from either costs no second read. No new API.
 *
 * The window runs from the start of yesterday through six days after today.
 * When the seat's courses share one zone, that zone decides each class's day;
 * when they span several, the reader's own zone does (the rule All classes
 * uses). "Today" is the projection's `generated_at` read in that zone, never
 * the browser clock. A class carries an interruption only when its payload
 * states one: Cancelled. The sessions payload carries no roll state.
 */
import { useQueries, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { attendanceApi, type Session } from "@/lib/api/attendance";
import { coursesApi, type Course } from "@/lib/api/courses";
import { classTitle } from "@/lib/class-name";
import { orderSessionIndexRecords } from "@/lib/session-index";
import { formatInCourseZone } from "@/lib/session-portal";
import { courseWorkspacePath } from "@/navigation/routes";

export type TeachingWeekShell = "instructor" | "adminTeaching";

export interface TeachingWeekClass {
  course: Pick<Course, "id" | "code" | "title">;
  session: Session;
  zone: string;
  title: string;
  notice: "Cancelled" | null;
  href: string;
}

export interface TeachingWeekDay {
  /** Days from today, in the grouping zone. */
  offset: number;
  /** `2026-10-13`, in the grouping zone. */
  date: string;
  label: string;
  classes: TeachingWeekClass[];
}

export type TeachingWeek =
  | { state: "pending" }
  | { state: "unavailable"; retry: () => void }
  | { state: "ready"; days: TeachingWeekDay[]; count: number };

/** `2026-10-13` for an instant, in the given zone. */
function zonedDay(instant: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone })
      .format(instant);
  } catch {
    return instant.toISOString().slice(0, 10);
  }
}

function utcDay(day: string): number {
  const [year, month, date] = day.split("-").map(Number);
  return Date.UTC(year, month - 1, date);
}

function dayLabel(day: string, offset: number): string {
  if (offset === -1) return "Yesterday";
  if (offset === 0) return "Today";
  if (offset === 1) return "Tomorrow";
  const [year, month, date] = day.split("-").map(Number);
  return new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })
    .format(new Date(Date.UTC(year, month - 1, date, 12)));
}

/** The class's start in its course zone; the zone is named when the week spans several. */
export function teachingWeekTime(row: TeachingWeekClass, withZone: boolean): string | null {
  if (!row.session.starts_at) return null;
  return formatInCourseZone(row.session.starts_at, row.zone, {
    hour: "numeric",
    minute: "2-digit",
    ...(withZone ? { timeZoneName: "short" as const } : {}),
  });
}

export function useTeachingWeek({
  shell,
  courseIds,
  reference,
  enabled = true,
}: {
  shell: TeachingWeekShell;
  /** The seat's current courses, from the projection. */
  courseIds: ReadonlySet<string>;
  /** The projection's `generated_at`. */
  reference: string;
  /** False until the projection has landed: the week reads after it. */
  enabled?: boolean;
}): TeachingWeek {
  const { me } = useAuth();
  const courses = useQuery({
    queryKey: ["session-index-courses", me?.tenant_id, me?.user_id, shell],
    queryFn: () => coursesApi.list({}),
    enabled: enabled && Boolean(me?.tenant_id && me?.user_id),
    retry: false,
  });
  const scoped = (courses.isError ? [] : courses.data ?? []).filter(course =>
    course.status !== "archived" && course.can_teach === true && courseIds.has(course.id));
  const schedules = useQueries({
    queries: scoped.map(course => ({
      queryKey: ["course-workspace-sessions", me?.tenant_id, me?.user_id, course.id],
      queryFn: () => attendanceApi.listSessions(course.id),
      retry: false,
    })),
  });

  const retry = () => {
    if (courses.isError) void courses.refetch();
    for (const schedule of schedules) if (schedule.isError) void schedule.refetch();
  };
  if (!enabled) return { state: "pending" };
  if (courses.isError) return { state: "unavailable", retry };
  if (courses.isPending || schedules.some(schedule => schedule.isPending)) return { state: "pending" };
  const unavailable = schedules.some((schedule, index) => schedule.isError
    || Boolean(schedule.data?.some(session => session.course_id !== scoped[index].id)));
  if (unavailable) return { state: "unavailable", retry };

  const now = new Date(reference);
  const zones = new Set(scoped.map(course => course.timezone ?? "UTC"));
  const groupZone = zones.size === 1 ? [...zones][0] : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = zonedDay(now, groupZone);
  const byDay = new Map<string, TeachingWeekDay>();
  const records = scoped
    .flatMap((course, index) => (schedules[index].data ?? []).map(session => ({ course, session })))
    .sort(orderSessionIndexRecords);
  for (const { course, session } of records) {
    const zone = course.timezone ?? "UTC";
    const day = session.starts_at ? zonedDay(new Date(session.starts_at), groupZone) : session.session_date;
    const offset = Math.round((utcDay(day) - utcDay(today)) / 86_400_000);
    if (offset < -1 || offset > 6) continue;
    const entry = byDay.get(day) ?? { offset, date: day, label: dayLabel(day, offset), classes: [] };
    entry.classes.push({
      course: { id: course.id, code: course.code, title: course.title },
      session,
      zone,
      title: classTitle(session, zone, { underDay: true }),
      notice: session.status === "cancelled" ? "Cancelled" : null,
      href: `${courseWorkspacePath(shell, course.id)}?session=${encodeURIComponent(session.id)}`,
    });
    byDay.set(day, entry);
  }
  const days = [...byDay.values()].sort((left, right) => left.offset - right.offset);
  return { state: "ready", days, count: days.reduce((total, day) => total + day.classes.length, 0) };
}
